import React, { useMemo } from "react";
import Select from "react-select";

export default function SearchableSelect({
  options = [],
  value = "",
  onChange,
  placeholder = "-- Select --",
  isClearable = true,
  isDisabled = false,
  height = "32px",
  minWidth,
  width = "100%",
  className = "",
  style = {},
  formatOption,
  filterOption,
  menuPortal = true,
  name,
  id,
}) {
  // Normalize options array into { value, label, group, raw }
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      if (typeof opt === "string" || typeof opt === "number") {
        return { value: opt, label: String(opt), raw: opt };
      }
      if (!opt) return { value: "", label: "", raw: opt };
      const val =
        opt.value !== undefined
          ? opt.value
          : opt.id !== undefined
          ? opt.id
          : opt.code !== undefined
          ? opt.code
          : opt.HEAD_CODE !== undefined
          ? opt.HEAD_CODE
          : opt.HEADName !== undefined
          ? opt.HEADName
          : opt.GroupCODE !== undefined
          ? opt.GroupCODE
          : opt.VehicleNo !== undefined
          ? opt.VehicleNo
          : opt;

      const lbl =
        opt.label !== undefined
          ? opt.label
          : opt.name !== undefined
          ? opt.name
          : opt.HEADName !== undefined
          ? opt.HEADName
          : opt.LedgerName !== undefined
          ? opt.LedgerName
          : opt.GroupName !== undefined
          ? opt.GroupName
          : opt.VehicleNo !== undefined
          ? opt.VehicleNo
          : opt.VehicleTypeName !== undefined
          ? opt.VehicleTypeName
          : String(val);

      const grp = opt.group || opt.GroupName || opt.GroupNAME || "";

      return {
        value: val,
        label: lbl,
        group: grp,
        raw: opt,
      };
    });
  }, [options]);

  // Find matching selected option
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object" && "value" in value) return value;
    const found = normalizedOptions.find(
      (opt) => String(opt.value).toLowerCase() === String(value).toLowerCase()
    );
    if (found) return found;
    // Fallback if not found in options
    return { value, label: String(value) };
  }, [value, normalizedOptions]);

  const customStyles = useMemo(
    () => ({
      container: (base) => ({
        ...base,
        width: width || "100%",
        minWidth: minWidth || "auto",
        ...style,
      }),
      control: (base, state) => ({
        ...base,
        minHeight: height,
        height: height,
        fontSize: "12.5px",
        borderColor: state.isFocused ? "#064E48" : "#cbd5e1",
        backgroundColor: isDisabled ? "#f8fafc" : "#ffffff",
        boxShadow: state.isFocused ? "0 0 0 1px #064E48" : "none",
        borderRadius: "4px",
        padding: "0",
        cursor: isDisabled ? "not-allowed" : "pointer",
        "&:hover": {
          borderColor: "#064E48",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        height: height,
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
      }),
      input: (base) => ({
        ...base,
        margin: "0",
        padding: "0",
        fontSize: "12.5px",
        color: "#1a202c",
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: "12px",
        color: "#94a3b8",
        margin: "0",
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: "12.5px",
        color: "#1a202c",
        margin: "0",
        fontWeight: 500,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: height,
      }),
      dropdownIndicator: (base) => ({
        ...base,
        padding: "2px 6px",
        color: "#64748b",
        svg: {
          width: "14px",
          height: "14px",
        },
        "&:hover": {
          color: "#064E48",
        },
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: "2px 4px",
        color: "#94a3b8",
        svg: {
          width: "12px",
          height: "12px",
        },
        "&:hover": {
          color: "#e53e3e",
        },
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
      }),
      menu: (base) => ({
        ...base,
        fontSize: "12px",
        zIndex: 99999,
        marginTop: "2px",
        borderRadius: "4px",
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.16)",
        border: "1px solid #cbd5e1",
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: "240px",
        padding: "2px 0",
      }),
      option: (base, state) => ({
        ...base,
        fontSize: "12px",
        padding: "6px 10px",
        backgroundColor: state.isSelected
          ? "#064E48"
          : state.isFocused
          ? "#e6fffa"
          : "#ffffff",
        color: state.isSelected ? "#ffffff" : "#1a202c",
        cursor: "pointer",
        "&:active": {
          backgroundColor: "#064E48",
          color: "#ffffff",
        },
      }),
    }),
    [height, width, minWidth, style, isDisabled]
  );

  const defaultFilterOption = (candidate, input) => {
    if (!input) return true;
    const q = input.toLowerCase().trim();
    const label = String(candidate.label || "").toLowerCase();
    const group = String(candidate.data?.group || "").toLowerCase();
    return label.includes(q) || group.includes(q);
  };

  const defaultFormatOptionLabel = (option, { context }) => {
    if (context === "value") {
      return option.label;
    }
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <span style={{ fontWeight: 500 }}>{option.label}</span>
        {option.group && (
          <span
            style={{
              fontSize: "10px",
              color: "#4a5568",
              backgroundColor: "#edf2f7",
              padding: "1px 6px",
              borderRadius: "3px",
              whiteSpace: "nowrap",
            }}
          >
            {option.group}
          </span>
        )}
      </div>
    );
  };

  const handleChange = (selected) => {
    if (!onChange) return;
    const val = selected ? selected.value : "";
    onChange(val, selected ? selected.raw || selected : null);
  };

  return (
    <Select
      id={id}
      name={name}
      className={`searchable_select_wrapper ${className}`}
      classNamePrefix="searchable_select"
      styles={customStyles}
      menuPortalTarget={menuPortal && typeof document !== "undefined" ? document.body : null}
      menuPosition="fixed"
      options={normalizedOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={true}
      openMenuOnFocus={true}
      filterOption={filterOption || defaultFilterOption}
      formatOptionLabel={formatOption || defaultFormatOptionLabel}
      noOptionsMessage={() => "No matching results found"}
    />
  );
}
